/**
 * rightPanel.store — Zustand UI-only slice for the right-side tools panel.
 *
 * Per FRONTEND_PLAN.md §10:
 *   stores/rightPanel.store.ts — live-context layers + active-agents drawer
 *
 * The right panel renders three sections (Project Manifest summary, Live
 * Context Layers, Active Agents). The first two are derived from server
 * state; this store only owns:
 *   - which context layers the user has TURNED ON for the current task
 *     (server state has the inventory + a `enabledByDefault` flag — the
 *     user's per-task overrides live here)
 *   - whether the panel is currently expanded or hidden
 *   - which sub-section is collapsed within the panel
 *
 * Layer overrides are persisted per-(employeeId, bindleId) so toggling a
 * layer in conversation A on Bindle X stays toggled when the user comes back
 * tomorrow — but a different Bindle starts clean.
 */
import { create } from 'zustand';
import { z } from 'zod';

const STORAGE_PREFIX = 'wayne.v1.rightPanel';

export type RightPanelSection =
  | 'projectManifest'
  | 'liveContextLayers'
  | 'activeAgents'
  | 'connections';

export type RightPanelView = 'context' | 'memory';

const PersistedSchema = z.object({
  version: z.literal(1),
  enabledLayers: z.array(z.string()),
  collapsedSections: z.array(
    z.enum(['projectManifest', 'liveContextLayers', 'activeAgents', 'connections']),
  ),
});

type Persisted = z.infer<typeof PersistedSchema>;

export interface RightPanelStoreState {
  /** Layer ids the user has explicitly turned ON for the current Bindle. */
  enabledLayers: ReadonlySet<string>;
  /** Sections the user has manually collapsed inside the right panel. */
  collapsedSections: ReadonlySet<RightPanelSection>;
  /** Which (employeeId, bindleId) the persisted state was hydrated for. */
  hydratedFor: { employeeId: string; bindleId: string } | null;
  /** Which view the right panel is currently showing. */
  activeView: RightPanelView;
  toggleLayer: (layerId: string) => void;
  setLayer: (layerId: string, enabled: boolean) => void;
  toggleSection: (section: RightPanelSection) => void;
  setActiveView: (view: RightPanelView) => void;
  toggleActiveView: () => void;
  hydrateFromStorage: (employeeId: string, bindleId: string) => void;
  reset: () => void;
}

function storageKey(employeeId: string, bindleId: string): string {
  return `${STORAGE_PREFIX}.${employeeId}.${bindleId}`;
}

function readPersisted(employeeId: string, bindleId: string): Persisted | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(employeeId, bindleId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const result = PersistedSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function writePersisted(
  employeeId: string,
  bindleId: string,
  value: Persisted,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      storageKey(employeeId, bindleId),
      JSON.stringify(value),
    );
  } catch {
    // ignore
  }
}

function persistFromState(state: RightPanelStoreState): void {
  if (!state.hydratedFor) return;
  const { employeeId, bindleId } = state.hydratedFor;
  writePersisted(employeeId, bindleId, {
    version: 1,
    enabledLayers: Array.from(state.enabledLayers),
    collapsedSections: Array.from(state.collapsedSections),
  });
}

export const useRightPanelStore = create<RightPanelStoreState>((set, get) => ({
  enabledLayers: new Set<string>(),
  collapsedSections: new Set<RightPanelSection>(),
  hydratedFor: null,
  activeView: 'context' as RightPanelView,
  toggleLayer: (layerId) => {
    const next = new Set(get().enabledLayers);
    if (next.has(layerId)) {
      next.delete(layerId);
    } else {
      next.add(layerId);
    }
    set({ enabledLayers: next });
    persistFromState(get());
  },
  setLayer: (layerId, enabled) => {
    const next = new Set(get().enabledLayers);
    if (enabled) {
      next.add(layerId);
    } else {
      next.delete(layerId);
    }
    set({ enabledLayers: next });
    persistFromState(get());
  },
  toggleSection: (section) => {
    const next = new Set(get().collapsedSections);
    if (next.has(section)) {
      next.delete(section);
    } else {
      next.add(section);
    }
    set({ collapsedSections: next });
    persistFromState(get());
  },
  setActiveView: (view) => {
    set({ activeView: view });
  },
  toggleActiveView: () => {
    set({ activeView: get().activeView === 'context' ? 'memory' : 'context' });
  },
  hydrateFromStorage: (employeeId, bindleId) => {
    const stored = readPersisted(employeeId, bindleId);
    set({
      hydratedFor: { employeeId, bindleId },
      enabledLayers: new Set<string>(stored?.enabledLayers ?? []),
      collapsedSections: new Set<RightPanelSection>(
        stored?.collapsedSections ?? [],
      ),
    });
  },
  reset: () =>
    set({
      enabledLayers: new Set<string>(),
      collapsedSections: new Set<RightPanelSection>(),
      hydratedFor: null,
      activeView: 'context' as RightPanelView,
    }),
}));
